import { useState } from 'react';
import { Keyboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';

const shortcutGroups = [
  {
    label: 'Navigation',
    shortcuts: [
      { keys: [`${mod}`, 'K'], description: 'Open Command Palette' },
      { keys: [`${mod}`, '/'], description: 'Focus search' },
    ],
  },
  {
    label: 'Chat',
    shortcuts: [
      { keys: ['Enter'], description: 'Send message' },
      { keys: ['Shift', 'Enter'], description: 'New line' },
      { keys: [`${mod}`, 'N'], description: 'New conversation' },
    ],
  },
  {
    label: 'Tools',
    shortcuts: [
      { keys: [`${mod}`, 'Enter'], description: 'Generate / Submit' },
      { keys: [`${mod}`, 'H'], description: 'Open history' },
      { keys: [`${mod}`, 'S'], description: 'Save / Star' },
      { keys: ['Esc'], description: 'Close / Go back' },
    ],
  },
  {
    label: 'General',
    shortcuts: [
      { keys: [`${mod}`, 'D'], description: 'Toggle dark mode' },
      { keys: ['?'], description: 'Show this cheat sheet' },
    ],
  },
];

export function KeyboardShortcutsSheet() {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Keyboard shortcuts" aria-label="Keyboard shortcuts">
          <Keyboard className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 pr-4">
            {shortcutGroups.map((group) => (
              <div key={group.label} className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  {group.label}
                </h3>
                <div className="space-y-1">
                  {group.shortcuts.map((shortcut, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-foreground">{shortcut.description}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, j) => (
                          <span key={j}>
                            {j > 0 && <span className="text-muted-foreground text-xs mx-0.5">+</span>}
                            <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-mono font-medium rounded border border-border bg-muted text-muted-foreground">
                              {key}
                            </kbd>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
